import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { map } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Modal } from '@spirit-testing/component-library';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { PriorityPillComponent, PriorityLevel } from '../../components/priority-pill/priority-pill.component';
import { TcStatusPillComponent, TcStatusKind } from '../../components/tc-status-pill/tc-status-pill.component';
import { LifecyclePillComponent, LifecycleKind } from '../../components/lifecycle-pill/lifecycle-pill.component';
import { RelativeTimePipe } from '../../pipes/relative-time.pipe';
import { TestCaseService } from '../../services/test-case.service';
import { SavedViewService } from '../../services/saved-view.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import {
  ExecutionSummary,
  TestCase,
  TestCasePriority,
  TestCaseStatus,
  TestExecutionStatus,
} from '../../models/test-case.model';
import { SavedView } from '../../models/saved-view.model';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import {
  FilterOption,
  FilterChange,
  TestCaseFilterBarComponent,
  TestCaseFilterKey,
} from './test-case-filter-bar/test-case-filter-bar.component';
import { SavedViewOption } from '../../shared/saved-views/saved-views-menu.component';
import { TestCaseImportModalComponent } from './import-modal/test-case-import-modal.component';
import { buildTestCaseSavedViewFiltersJson, extractJiraKey, parseTestCaseSavedViewFilters } from './test-case.utils';
import { ProjectRoleService } from '../../services/project-role.service';
import { ListFocusScrollDirective } from '../../shared/scroll-container/list-focus-scroll.directive';
import { isTransientError } from '../../core/connectivity/transient-error';

interface TestCaseRow {
  id: string;
  key: string;
  title: string;
  description: string;
  source: string | null;
  stepCount: number;
  authorName: string | null;
  updatedAt: string;
  priority: TestCasePriority;
  priorityLabel: string;
  priorityClass: string;
  priorityLevel: PriorityLevel;
  executionStatus: ExecutionFilterValue;
  executionLabel: string;
  executionClass: string;
  executionKind: TcStatusKind;
  status: TestCaseStatus;
  statusLabel: string;
  statusClass: string;
  lifecycleKind: LifecycleKind;
  /**
   * Vorlokalisiert-lowercased Haystack fuer die Freitextsuche. Enthaelt Key,
   * Titel, Beschreibung und den vollen Anforderungs-Text (nicht nur den
   * daraus extrahierten Jira-Key, NEX-1057 Review). Einmal in toRow gebaut,
   * damit filteredRows pro Zeichen des Sucheingaben nicht erneut allokiert.
   */
  searchIndex: string;
}

// Ausführungsstatus inkl. "noch nie ausgeführt" (NONE) — Basis für den
// Ausführungs-Filter (NEX-407), passend zur Ausführungs-Spalte (NEX-405).
// Ein letzter Lauf mit Ergebnis NOT_RUN gilt wie "nie ausgeführt" (NEX-1389).
type ExecutionFilterValue = Exclude<TestExecutionStatus, 'NOT_RUN'> | 'NONE';

// Feste Reihenfolge für die Filter-Optionen, damit die Dropdowns unabhängig
// von der Datenreihenfolge stabil und nach Schweregrad sortiert sind.
const EXECUTION_ORDER: readonly ExecutionFilterValue[] = ['PASSED', 'FAILED', 'BLOCKED', 'RUNNING', 'SKIPPED', 'NONE'];
const PRIORITY_ORDER: readonly TestCasePriority[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
// Lifecycle-Reihenfolge (NEX-444), passend zur Status-Spalte: DRAFT → READY →
// ACTIVE → DEPRECATED, damit Dropdown-Optionen stabil und nach Reifegrad sortiert sind.
const STATUS_ORDER: readonly TestCaseStatus[] = ['DRAFT', 'READY', 'ACTIVE', 'DEPRECATED'];

const PRIORITY_RANK: Record<TestCasePriority, number> = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 };
const EXECUTION_RANK: Record<ExecutionFilterValue, number> = {
  PASSED: 0,
  FAILED: 1,
  BLOCKED: 2,
  RUNNING: 3,
  SKIPPED: 4,
  NONE: 5,
};
const STATUS_RANK: Record<TestCaseStatus, number> = { DRAFT: 0, READY: 1, ACTIVE: 2, DEPRECATED: 3 };

export type TestCaseSortCol =
  | 'key'
  | 'title'
  | 'source'
  | 'stepCount'
  | 'priority'
  | 'execution'
  | 'status'
  | 'authorName';
type SortDir = 'asc' | 'desc';
interface TableSort {
  col: TestCaseSortCol | null;
  dir: SortDir;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_ROUTE_KEY = 'test-cases';

// Pass/Fail-Farben aus dem Design-Bundle (page-testfaelle.jsx:89-90), als
// Theme-Variable mit Hex-Fallback, damit die Kennzahlen ohne Theme korrekt sind.
const STAT_PASSED_COLOR = 'var(--to-green-fg, #15803d)';
const STAT_FAILED_COLOR = 'var(--to-red-fg, #b91c1c)';

@Component({
  selector: 'app-test-case',
  imports: [
    ListFocusScrollDirective,
    RouterLink,
    TranslatePipe,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    Modal,
    ProjectNavComponent,
    PageHeaderComponent,
    StatGroupComponent,
    AvatarComponent,
    CodeIdChipComponent,
    PriorityPillComponent,
    TcStatusPillComponent,
    LifecyclePillComponent,
    RelativeTimePipe,
    SearchBarComponent,
    TestCaseFilterBarComponent,
    TestCaseImportModalComponent,
  ],
  templateUrl: './test-case.component.html',
  styleUrl: './test-case.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly testCaseService = inject(TestCaseService);
  private readonly savedViewService = inject(SavedViewService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly pageSizePref = inject(PageSizePreferenceService);

  readonly isProjectReader = this.projectRoleService.isProjectReader;

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly projectId: string;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly loadUnavailable = signal(false);
  readonly deleteError = signal(false);
  readonly searchTerm = signal('');
  readonly pageIndex = signal(0);
  readonly pageSize = signal(this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE));

  // NEX-1057: die Filter sind seit der Umstellung auf Single-Select-Dropdowns
  // faktisch einwertig — der State bleibt als Array, damit gespeicherte Ansichten
  // (Format seit NEX-788, TestCaseSavedViewFilters) rueckwaerts-kompatibel gelesen
  // werden koennen. Legacy-Multi-Value-Werte werden beim Laden auf den ersten
  // Wert gekappt, siehe onSavedViewSelected.
  // `selectedStatus` filtert seit NEX-444 den Lifecycle-Status (DRAFT/READY/…);
  // der Ausführungs-Filter heißt jetzt explizit `selectedExecution`.
  readonly selectedStatus = signal<string[]>([]);
  readonly selectedExecution = signal<string[]>([]);
  readonly selectedPriority = signal<string[]>([]);
  readonly selectedAuthor = signal<string[]>([]);

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);

  readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) })),
  );

  readonly showDeleteModal = signal(false);
  readonly rowToDelete = signal<TestCaseRow | null>(null);

  readonly showImportModal = signal(false);

  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  private readonly testCases = signal<TestCase[]>([]);
  // Kopfbereich-Aggregat. null, solange (noch) nicht geladen oder bei Fehler —
  // die Kennzahlen sind sekundär und sollen die Liste nicht blockieren.
  private readonly summary = signal<ExecutionSummary | null>(null);

  // Aktive Locale für die locale-bewusste Relative-Zeit-Pipe ("zuletzt").
  readonly locale = computed(() => this.currentLang() || 'de');

  readonly rows = computed<TestCaseRow[]>(() => {
    // Read the active language so the computed re-runs on language change and
    // the translated pill/label strings stay in sync.
    this.currentLang();
    return this.testCases().map((tc) => this.toRow(tc));
  });

  readonly statItems = computed<StatItem[]>(() => {
    this.currentLang();
    const s = this.summary();
    if (!s) return [];
    return [
      { label: this.translate.instant('testCases.stats.total'), value: s.total },
      { label: this.translate.instant('testCases.stats.passed'), value: s.passed, color: STAT_PASSED_COLOR },
      { label: this.translate.instant('testCases.stats.failed'), value: s.failed, color: STAT_FAILED_COLOR },
      { label: this.translate.instant('testCases.stats.passRate'), value: `${s.passRate}%` },
    ];
  });

  readonly statsAriaLabel = computed(() => {
    this.currentLang();
    const s = this.summary();
    if (!s) return '';
    return this.translate.instant('testCases.stats.ariaLabel', {
      total: s.total,
      passed: s.passed,
      failed: s.failed,
      passRate: s.passRate,
    });
  });

  // Filter-Optionen aus der geladenen Liste ableiten (nur tatsächlich
  // vorhandene Werte anbieten), sortiert für stabile Dropdowns (NEX-407).
  readonly executionOptions = computed<FilterOption[]>(() => {
    this.currentLang();
    return this.enumOptions(
      this.rows().map((r) => r.executionStatus),
      EXECUTION_ORDER,
      'testCases.executionStatus',
    );
  });

  readonly statusOptions = computed<FilterOption[]>(() => {
    this.currentLang();
    return this.enumOptions(
      this.rows().map((r) => r.status),
      STATUS_ORDER,
      'testCases.status',
    );
  });

  readonly priorityOptions = computed<FilterOption[]>(() => {
    this.currentLang();
    return this.enumOptions(
      this.rows().map((r) => r.priority),
      PRIORITY_ORDER,
      'testCases.priority',
    );
  });

  private enumOptions<T extends string>(values: readonly T[], order: readonly T[], i18nPrefix: string): FilterOption[] {
    const present = new Set(values);
    return order
      .filter((v) => present.has(v))
      .map((v) => ({ value: v, label: this.translate.instant(`${i18nPrefix}.${v}`) }));
  }

  readonly authorOptions = computed<FilterOption[]>(() => toLabelOptions(this.rows().map((r) => r.authorName)));

  // NEX-1057 Review (fboudouhi): der aktive Filterzustand ist die Basis fuer den
  // Reset-Button in der Filterleiste.
  readonly hasActiveFilters = computed(
    () =>
      this.searchTerm().length > 0 ||
      this.selectedStatus().length > 0 ||
      this.selectedExecution().length > 0 ||
      this.selectedPriority().length > 0 ||
      this.selectedAuthor().length > 0,
  );

  readonly filteredRows = computed<TestCaseRow[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const status = this.selectedStatus();
    const execution = this.selectedExecution();
    const priority = this.selectedPriority();
    const author = this.selectedAuthor();

    return this.rows().filter((row) => {
      if (term) {
        // NEX-1057 Review (fboudouhi): sowohl den vollen Anforderungs-Text
        // (row.requirementLabel) als auch den extrahierten Jira-Key (row.source)
        // in den Haystack aufnehmen. Vorher lief die Suche nach „SSO" fuer eine
        // Anforderung „NEX-42 Login mit SSO" leer, weil row.source nur den Key
        // enthielt. Ausserdem row.key aufgenommen (Design page-testfaelle.jsx
        // sucht ueber id+titel+autor).
        if (!row.searchIndex.includes(term)) return false;
      }
      if (status.length && !status.includes(row.status)) return false;
      if (execution.length && !execution.includes(row.executionStatus)) return false;
      if (priority.length && !priority.includes(row.priority)) return false;
      if (author.length && (row.authorName === null || !author.includes(row.authorName))) return false;
      return true;
    });
  });

  readonly total = computed(() => this.filteredRows().length);
  readonly unfilteredTotal = computed(() => this.rows().length);

  readonly tableSort = signal<TableSort>({ col: null, dir: 'asc' });

  readonly sortedRows = computed<TestCaseRow[]>(() => sortRows(this.filteredRows(), this.tableSort()));

  readonly pagedRows = computed<TestCaseRow[]>(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.sortedRows().slice(start, start + this.pageSize());
  });

  sortIcon(col: TestCaseSortCol): string {
    const s = this.tableSort();
    if (s.col !== col) return 'unfold_more';
    return s.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  sortAria(col: TestCaseSortCol): 'ascending' | 'descending' | 'none' {
    const s = this.tableSort();
    if (s.col !== col) return 'none';
    return s.dir === 'asc' ? 'ascending' : 'descending';
  }

  sortBy(col: TestCaseSortCol): void {
    const current = this.tableSort();
    this.tableSort.set({ col, dir: current.col === col && current.dir === 'asc' ? 'desc' : 'asc' });
    this.pageIndex.set(0);
  }

  readonly rangeStart = computed(() => (this.total() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1));
  readonly rangeEnd = computed(() => Math.min(this.total(), (this.pageIndex() + 1) * this.pageSize()));
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize())));

  readonly hasResults = computed(() => this.total() > 0);

  // ngx-translate runs without an ICU/plural compiler here, so the singular vs.
  // plural form is selected by key instead of inline plural syntax. The noun
  // follows "von {total}", so the unfiltered total decides the form.
  readonly resultCountKey = computed(() =>
    this.unfilteredTotal() === 1 ? 'testCases.resultCountOne' : 'testCases.resultCountOther',
  );

  constructor() {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  ngOnInit(): void {
    this.loadTestCases();
    this.loadSummary();
    this.loadSavedViews();
  }

  private loadSavedViews(): void {
    this.savedViewService.list(this.projectId, 'TEST_CASES').subscribe({
      next: (views) => this.savedViews.set(views),
      error: () => this.savedViews.set([]),
    });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseTestCaseSavedViewFilters(view.filtersJson);
    const parts = [
      this.describeEnumFilter(filters.status, 'testCases.status'),
      this.describeEnumFilter(filters.execution, 'testCases.executionStatus'),
      this.describeEnumFilter(filters.priority, 'testCases.priority'),
      this.describeFreetextFilter(filters.author),
    ].filter((s): s is string => !!s);
    if (parts.length === 0) {
      return this.translate.instant('testCases.savedViews.summaryAll');
    }
    return parts.join(' · ');
  }

  private describeEnumFilter(values: string[], i18nPrefix: string): string | null {
    if (values.length === 0) return null;
    return values.map((v) => this.translate.instant(`${i18nPrefix}.${v}`)).join(', ');
  }

  private describeFreetextFilter(values: string[]): string | null {
    return values.length === 0 ? null : values.join(', ');
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseTestCaseSavedViewFilters(view.filtersJson);
    this.searchTerm.set(filters.query ?? '');
    // NEX-1057 Review (fboudouhi Blocker 1): eine vor der Single-Select-
    // Umstellung gespeicherte Ansicht kann pro Filter mehrere Werte tragen.
    // Auf den ersten Wert kappen — sonst zeigt das Dropdown "Entwurf",
    // waehrend die Tabelle sowohl DRAFT als auch READY filtert.
    this.selectedStatus.set(filters.status.slice(0, 1));
    this.selectedExecution.set(filters.execution.slice(0, 1));
    this.selectedPriority.set(filters.priority.slice(0, 1));
    this.selectedAuthor.set(filters.author.slice(0, 1));
    this.pageIndex.set(0);
    this.activeSavedViewName.set(view.name);
  }

  onSavedViewSaved(name: string): void {
    const filtersJson = buildTestCaseSavedViewFiltersJson({
      query: this.searchTerm() || null,
      status: this.selectedStatus(),
      execution: this.selectedExecution(),
      priority: this.selectedPriority(),
      author: this.selectedAuthor(),
    });
    this.savedViewService.upsert(this.projectId, { name, context: 'TEST_CASES', filtersJson }).subscribe({
      next: (saved) => {
        this.savedViews.update((list) => {
          const rest = list.filter((v) => v.name !== saved.name);
          return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.activeSavedViewName.set(saved.name);
        this.savedViewsError.set(null);
      },
      error: () => this.savedViewsError.set(this.translate.instant('testCases.savedViews.saveError')),
    });
  }

  /**
   * Das Loeschen selbst uebernimmt der generische SavedViewsMenuComponent (NEX-786) —
   * hier wird nur noch der lokale State nach einem erfolgreichen Loeschvorgang bereinigt.
   */
  onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      this.activeSavedViewName.set(null);
    }
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('testCases.savedViews.deleteError'));
  }

  private clearActiveView(): void {
    if (this.activeSavedViewName() !== null) {
      this.activeSavedViewName.set(null);
    }
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  openImportModal(): void {
    this.showImportModal.set(true);
  }

  onImported(): void {
    this.pageIndex.set(0);
    this.loadTestCases();
    this.loadSummary();
  }

  onFilterChange({ key, value }: FilterChange): void {
    this.filterSignal(key).set(value ? [value] : []);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  private filterSignal(key: TestCaseFilterKey) {
    switch (key) {
      case 'status':
        return this.selectedStatus;
      case 'execution':
        return this.selectedExecution;
      case 'priority':
        return this.selectedPriority;
      case 'author':
        return this.selectedAuthor;
    }
  }

  // Nach einem (Re-)Load Filterwerte verwerfen, die es in der neuen Liste nicht
  // mehr gibt — sonst bleibt ein unsichtbarer Filter aktiv und liefert 0 Treffer,
  // ohne dass der Nutzer die Ursache im Dropdown sehen kann.
  private pruneStaleFilters(): void {
    const keep = (signal: typeof this.selectedStatus, options: FilterOption[]): void => {
      const available = new Set(options.map((o) => o.value));
      const next = signal().filter((v) => available.has(v));
      if (next.length !== signal().length) signal.set(next);
    };
    keep(this.selectedStatus, this.statusOptions());
    keep(this.selectedExecution, this.executionOptions());
    keep(this.selectedPriority, this.priorityOptions());
    keep(this.selectedAuthor, this.authorOptions());
  }

  /**
   * NEX-1057 Review (fboudouhi): setzt Suche und alle Filter in einem Schritt
   * zurueck. Zusammen mit dem sichtbaren Reset-Button erspart es dem Nutzer,
   * jedes Dropdown einzeln auf den Placeholder zu stellen — besonders wichtig
   * fuer geladene Ansichten mit mehreren aktiven Filtern.
   */
  onResetFilters(): void {
    this.searchTerm.set('');
    this.selectedStatus.set([]);
    this.selectedExecution.set([]);
    this.selectedPriority.set([]);
    this.selectedAuthor.set([]);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  onPageChange(index: number): void {
    this.pageIndex.set(index);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
  }

  onRowClick(row: TestCaseRow): void {
    this.router.navigate(['/projects', this.projectId, 'test-cases', row.id, 'edit']);
  }

  onRowKeydown(event: Event, row: TestCaseRow): void {
    if (event.target !== event.currentTarget) return;
    this.onRowClick(row);
  }

  onDeleteRequest(row: TestCaseRow): void {
    this.rowToDelete.set(row);
    this.showDeleteModal.set(true);
  }

  onCancelDelete(): void {
    this.showDeleteModal.set(false);
    this.rowToDelete.set(null);
  }

  onConfirmDelete(): void {
    const row = this.rowToDelete();
    if (!row) return;
    this.deleteError.set(false);
    this.testCaseService
      .delete(this.projectId, row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.showDeleteModal.set(false);
          this.rowToDelete.set(null);
          // Deleting the last row on a trailing page would otherwise leave the
          // user on an empty page, so reload from the first page.
          this.pageIndex.set(0);
          this.loadTestCases();
          // Das Löschen verändert die Kennzahlen — Aggregat mitziehen.
          this.loadSummary();
        },
        error: () => {
          this.showDeleteModal.set(false);
          this.rowToDelete.set(null);
          this.deleteError.set(true);
        },
      });
  }

  retryLoad(): void {
    this.loadTestCases();
    this.loadSummary();
  }

  private loadTestCases(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.loadUnavailable.set(false);
    this.testCaseService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (cases) => {
          this.testCases.set(cases);
          this.pruneStaleFilters();
          this.loading.set(false);
        },
        error: (error: unknown) => {
          this.testCases.set([]);
          this.pruneStaleFilters();
          if (isTransientError(error)) {
            this.loadUnavailable.set(true);
          } else {
            this.loadError.set(true);
          }
          this.loading.set(false);
        },
      });
  }

  private loadSummary(): void {
    this.testCaseService
      .getExecutionSummary(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => this.summary.set(summary),
        // Sekundäre Kennzahlen: bei Fehler einfach ausblenden, kein Seiten-Fehler.
        error: () => this.summary.set(null),
      });
  }

  private toRow(tc: TestCase): TestCaseRow {
    const execution = toExecutionFilterValue(tc.lastExecutionStatus);
    const description = (tc.description ?? '').trim();
    const requirementLabel = tc.storyJiraKey ?? tc.requirementLabel ?? tc.requirementId ?? '';
    return {
      id: tc.id,
      key: tc.key,
      title: tc.title,
      description,
      source: extractJiraKey(tc.storyJiraKey ?? tc.requirementLabel ?? tc.requirementId ?? null),
      stepCount: tc.stepCount ?? 0,
      authorName: tc.authorName ?? null,
      updatedAt: tc.updatedAt,
      priority: tc.priority,
      priorityLabel: this.translate.instant(`testCases.priority.${tc.priority}`),
      priorityClass: pillClass('priority', tc.priority),
      priorityLevel: TC_PRIORITY_TO_LEVEL[tc.priority] ?? 'mittel',
      executionStatus: execution,
      executionLabel: this.translate.instant(`testCases.executionStatus.${execution}`),
      executionClass: pillClass('exec', execution),
      executionKind: TC_EXECUTION_TO_KIND[execution] ?? 'draft',
      status: tc.status,
      statusLabel: this.translate.instant(`testCases.status.${tc.status}`),
      statusClass: pillClass('status', tc.status),
      lifecycleKind: TC_STATUS_TO_LIFECYCLE[tc.status] ?? 'draft',
      searchIndex: [tc.key, tc.title, description, requirementLabel].join(' ').toLowerCase(),
    };
  }
}

const TC_PRIORITY_TO_LEVEL: Record<TestCasePriority, PriorityLevel> = {
  LOW: 'niedrig',
  MEDIUM: 'mittel',
  HIGH: 'hoch',
  CRITICAL: 'kritisch',
};

const TC_EXECUTION_TO_KIND: Record<ExecutionFilterValue, TcStatusKind> = {
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  RUNNING: 'running',
  SKIPPED: 'draft',
  NONE: 'draft',
};

function toExecutionFilterValue(status: TestExecutionStatus | null | undefined): ExecutionFilterValue {
  return !status || status === 'NOT_RUN' ? 'NONE' : status;
}

const TC_STATUS_TO_LIFECYCLE: Record<TestCaseStatus, LifecycleKind> = {
  DRAFT: 'draft',
  READY: 'ready',
  ACTIVE: 'active',
  DEPRECATED: 'deprecated',
};

function pillClass(modifier: 'priority' | 'exec' | 'status', value: string | null | undefined): string {
  return `tc-pill tc-pill--${modifier}-${(value ?? 'unknown').toLowerCase()}`;
}

function sortRows(rows: TestCaseRow[], sort: TableSort): TestCaseRow[] {
  const { col, dir } = sort;
  if (!col) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    let cmp: number;
    switch (col) {
      case 'priority':
        cmp = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
        break;
      case 'execution':
        cmp = EXECUTION_RANK[a.executionStatus] - EXECUTION_RANK[b.executionStatus];
        break;
      case 'status':
        cmp = STATUS_RANK[a.status] - STATUS_RANK[b.status];
        break;
      case 'stepCount':
        cmp = a.stepCount - b.stepCount;
        break;
      case 'key':
      case 'source':
        cmp = (a[col] ?? '').localeCompare(b[col] ?? '', undefined, { numeric: true, sensitivity: 'base' });
        break;
      default:
        cmp = (a[col] ?? '').localeCompare(b[col] ?? '', undefined, { sensitivity: 'base' });
    }
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

// Freitext-Werte (Quelle, Autor) zu eindeutigen, alphabetisch sortierten
// Filter-Optionen verdichten; Label entspricht dem Wert. null wird verworfen.
function toLabelOptions(values: (string | null)[]): FilterOption[] {
  const distinct = [...new Set(values.filter((v): v is string => v !== null))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );
  return distinct.map((value) => ({ value, label: value }));
}
